import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../layouts/DashboardLayout";
import StatusDonutChart from "./StatusDonutChart";
import WeeklyTrendsChart from "./WeeklyTrendsChart";
import { useAuth } from "../../context/AuthContext";
import {
  fetchDashboardStats,
  fetchActiveHackathon,
  fetchIdeaStatusBreakdown,
  fetchWeeklyTrends,
  fetchReadiness,
  fetchFeedbackInfo,
  type DashboardStats,
  type ActiveHackathon,
  type IdeaStatusBreakdown,
  type WeeklyTrend,
  type ReadinessData,
  type FeedbackInfo,
} from "../../api/dashboard";

// ✅ Dynamic greeting based on time of day
function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

// ✅ Dynamic date string
function getTodayLabel(): string {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

// Radial progress SVG circumference for r=52: 2π×52 ≈ 326.73
const CIRCUMFERENCE = 326.73;

function RadialProgress({ percentage }: { percentage: number }) {
  const offset = CIRCUMFERENCE - (percentage / 100) * CIRCUMFERENCE;
  return (
    <div className="relative w-32 h-32 flex items-center justify-center">
      <svg className="w-32 h-32 transform -rotate-90">
        {/* ✅ camelCase SVG attributes */}
        <circle
          cx="64"
          cy="64"
          r="52"
          stroke="#E5E7EB"
          strokeWidth="12"
          fill="transparent"
        />
        <circle
          cx="64"
          cy="64"
          r="52"
          stroke="#0058AB"
          strokeWidth="12"
          fill="transparent"
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <p className="text-2xl font-normal text-[#1A1F36]">{percentage}%</p>
        <p className="text-xs text-[#8B93A8]">Complete</p>
      </div>
    </div>
  );
}

const STEP_COLORS = [
  "bg-teal-600",
  "bg-indigo-600",
  "bg-red-500",
  "bg-purple-600",
  "bg-orange-500",
];

export default function Dashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [activeHackathon, setActiveHackathon] =
    useState<ActiveHackathon | null>(null);
  const [ideaBreakdown, setIdeaBreakdown] = useState<IdeaStatusBreakdown[]>([]);
  const [monthlyTrends, setMonthlyTrends] = useState<WeeklyTrend[]>([]);
  const [readiness, setReadiness] = useState<ReadinessData | null>(null);
  const [feedbackInfo, setFeedbackInfo] = useState<FeedbackInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    Promise.all([
      fetchDashboardStats(token),
      fetchActiveHackathon(token),
      fetchIdeaStatusBreakdown(token),
      fetchWeeklyTrends(token),
      fetchReadiness(token),
      fetchFeedbackInfo(token),
    ])
      .then(([s, ah, ib, mt, r, fi]) => {
        setStats(s);
        setActiveHackathon(ah);
        setIdeaBreakdown(ib);
        setMonthlyTrends(mt);
        setReadiness(r);
        setFeedbackInfo(fi);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  const progressBarWidth = (value: number, max: number) =>
    `${Math.min(Math.round((value / Math.max(max, 1)) * 100), 100)}%`;

  return (
    <DashboardLayout>
      <div className="space-y-6 sm:space-y-8 max-w-screen-xl mx-auto w-full px-4 sm:px-6 lg:px-8">
        {/* SECTION 1 — GREETING */}
        <div className="shadow-sm rounded-[16px] p-4 sm:p-6 lg:p-8">
          {/* ✅ Dynamic date */}
          <p className="text-xs text-gray-500 uppercase tracking-wide">
            {getTodayLabel()}
          </p>
          {/* ✅ Dynamic greeting */}
          <h1 className="text-3xl font-normal text-gray-800 mt-2">
            {getGreeting()},{" "}
            <span className="text-blue-600">{user?.name || "User"}</span>
          </h1>
          <p className="text-gray-500 mt-2">Your hackathon activity snapshot</p>
        </div>

        {/* SECTION 2 — STAT CARDS */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 gap-6 ${
            !loading && stats?.upcomingDaysLeft == null
              ? "lg:grid-cols-3"
              : "lg:grid-cols-4"
          }`}
        >
          {/* Upcoming Hackathon — only shown when there is one */}
          {(loading || stats?.upcomingDaysLeft != null) && (
            <div className="bg-white shadow-[0_2px_10px_rgba(0,0,0,0.1)] rounded-[16px] p-6">
              <h2 className="text-gray-600 text-sm">Upcoming Hackathon</h2>
              <p className="text-3xl font-semibold text-blue-600 mt-2">
                {loading ? "—" : stats!.upcomingDaysLeft}
              </p>
              <p className="text-sm text-gray-500">days left</p>
            </div>
          )}

          <div className="bg-white shadow-[0_2px_10px_rgba(0,0,0,0.1)] rounded-[16px] p-6">
            <h2 className="text-gray-600 text-sm">Active Registrations</h2>
            {/* ✅ Dynamic registrations */}
            <p className="text-3xl font-semibold text-blue-600 mt-2">
              {loading ? "—" : (stats?.activeRegistrations ?? 0)}
            </p>
            <p className="text-sm text-gray-500">programs joined</p>
          </div>

          <div className="bg-white shadow-[0_2px_10px_rgba(0,0,0,0.1)] rounded-[16px] p-6">
            <h2 className="text-gray-600 text-sm">Idea Status</h2>
            {/* ✅ Dynamic idea count */}
            <p className="text-3xl font-semibold text-yellow-500 mt-2">
              {loading ? "—" : (stats?.totalIdeas ?? 0)}
            </p>
            <p className="text-sm text-gray-500">total submissions</p>
          </div>

          <div className="bg-white shadow-[0_2px_10px_rgba(0,0,0,0.1)] rounded-[16px] p-6">
            <h2 className="text-gray-600 text-sm">Mentor Activity</h2>
            <p className="text-3xl font-semibold text-purple-500 mt-2">
              {loading ? "—" : (stats?.mentorSessions ?? 0)}
            </p>
            <p className="text-sm text-gray-500">active sessions</p>
          </div>
        </div>

        {/* SECTION 3 — PROGRAM PROGRESS */}
        {(() => {
  if (loading)
    return (
      <div className="bg-white rounded-[16px] p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.05)] animate-pulse">
        <div className="h-5 bg-gray-100 rounded w-40 mb-4" />
        <div className="h-7 bg-gray-100 rounded w-72 mb-2" />
        <div className="h-4 bg-gray-100 rounded w-48" />
      </div>
    );

          if (!activeHackathon) return (
            <div className="bg-white rounded-[16px] p-8 shadow-sm text-center">
              <p className="text-gray-400 text-lg">You are not registered in any active hackathon</p>
              <button
                onClick={() => navigate("/user-dashboard/hackathons")}
                className="mt-4 bg-[#0058AB] text-white px-6 py-2 rounded"
              >
                Browse Hackathons
              </button>
            </div>
          );

  const {
    currentPhase,
    title,
    organizerName,
    daysLeft,
    nextPhaseName,
    nextPhaseDate,
    progressSteps,
    team,
  } = activeHackathon;

  return (
    <div className="bg-white rounded-[16px] p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.05)] w-full">

      {/* ✅ TOP ROW */}
      <div className="flex flex-row sm:flex-row items-start justify-between gap-4">
        <div>
          <span className="relative pl-6 px-4 py-2 text-xs font-medium text-[#0058AB] bg-blue-50 rounded-full">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#0058AB]/90" />
            {currentPhase}
          </span>

          <h2 className="text-lg sm:text-xl md:text-2xl font-medium text-[#1A1F36] mt-4">
            {title}
          </h2>

          <p className="text-[#6E7891] mt-1">{organizerName}</p>
        </div>

        <div className="text-left sm:text-right">
          <p className="text-orange-500 text-sm font-medium flex items-center gap-1 sm:justify-end">
            <span>⏱</span> {daysLeft} day{daysLeft !== 1 ? "s" : ""} left
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Next: {nextPhaseName} · {nextPhaseDate}
          </p>
        </div>
      </div>

      {/* ✅ PROGRESS STEPS */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mt-10 mb-6 gap-0">

        {progressSteps.map((step, i) => (
          <div key={step.label} className="flex flex-row md:flex-col items-center w-full md:w-auto md:flex-1 relative">

            {/* STEP CONTENT */}
            <div className="flex flex-col items-center min-w-[120px]">

              {/* ICON */}
              {step.status === "completed" ? (
                <div className="w-10 h-10 rounded-full bg-[#0058AB] flex items-center justify-center text-white">
                  ✓
                </div>
              ) : step.status === "current" ? (
                <div className="w-10 h-10 rounded-full border-2 border-[#0058AB]/20 flex items-center justify-center">
                  <div className="w-3 h-3 bg-[#0058AB] rounded-full" />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-full border-2 border-gray-300 text-gray-400 flex items-center justify-center">
                  {i + 1}
                </div>
              )}

              {/* LABEL */}
              <p className="text-sm mt-2 text-center">
                {step.label}
              </p>

              {/* STATUS */}
              <p
                className={`text-xs ${
                  step.status === "completed"
                    ? "text-[#0058AB]/60"
                    : step.status === "current"
                    ? "text-[#0058AB]"
                    : "text-gray-400"
                }`}
              >
                {step.status === "completed"
                  ? "Completed"
                  : step.status === "current"
                  ? "Current"
                  : "Upcoming"}
              </p>
            </div>

            {/* CONNECTING LINE - Hidden on mobile, shown on md and up */}
            {i < progressSteps.length - 1 && (
              <div
                className={`hidden md:block absolute top-5 left-[calc(50%+45px)] right-[-25%] h-1 ${
                  step.status === "completed"
                    ? "bg-[#0058AB]"
                    : "bg-[#e4e8ee]"
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* ✅ TEAM + BUTTON */}
      {team && (
        <div className="border-t pt-6 mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              {team.members.map((m, i) => (
                <div
                  key={i}
                  className={`w-8 h-8 rounded-full ${m.color} text-white text-xs flex items-center justify-center`}
                >
                  {m.initials}
                </div>
              ))}
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700">
                {team.name}
              </p>
              <p className="text-xs text-gray-400">
                {team.memberCount} member{team.memberCount !== 1 ? "s" : ""}
              </p>
            </div>
          </div>

                  <button
                    onClick={() => navigate("/user-dashboard/tracking-screen", { state: { hackathonId: activeHackathon._id } })}
                    className="bg-[#0058AB] text-white px-6 py-2 rounded shadow-md flex items-center gap-2"
                  >
                    Continue <span>→</span>
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {/* SECTION 4 — READINESS */}
        {readiness && (
          <div className="bg-white rounded-2xl shadow-sm p-6 w-full">
            <p className="text-xs font-normal tracking-widest text-[#8B93A8]">
              INNOVATION PROGRESS
            </p>
            <h2 className="text-xl font-medium text-[#1A1F36] mt-2">
              Hackathon Readiness
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-6">
              {/* ✅ Dynamic radial progress */}
              <div className="flex items-center justify-center">
                <RadialProgress percentage={readiness.percentage} />
              </div>

              <div className="md:col-span-2 space-y-[16px] pr-2">
                {/* ✅ Dynamic progress bars */}
                <div>
                  <div className="flex justify-between text-sm text-gray-700">
                    <p>Ideas Submitted</p>
                    <p className="text-[#1A1F36]">{readiness.ideasSubmitted}</p>
                  </div>
                  <div className="h-2 bg-gray-200 rounded mt-1">
                    <div
                      className="h-2 bg-[#0058AB] rounded"
                      style={{
                        width: progressBarWidth(readiness.ideasSubmitted, 10),
                      }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-sm text-gray-700">
                    <p>Team Formed</p>
                    <p className="text-[#1A1F36]">{readiness.teamFormed}</p>
                  </div>
                  <div className="h-2 bg-gray-200 rounded mt-1">
                    <div
                      className="h-2 bg-[#00C2C9] rounded"
                      style={{ width: readiness.teamFormed ? "100%" : "0%" }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-sm text-gray-700">
                    <p>Mentor Sessions</p>
                    <p className="text-[#1A1F36]">{readiness.mentorSessions}</p>
                  </div>
                  <div className="h-2 bg-gray-200 rounded mt-1">
                    <div
                      className="h-2 bg-[#6A4FA3] rounded"
                      style={{
                        width: progressBarWidth(readiness.mentorSessions, 5),
                      }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-sm text-gray-700">
                    <p>Milestones Hit</p>
                    <p className="text-[#1A1F36]">
                      {readiness.milestonesHit}/{readiness.milestonesTotal}
                    </p>
                  </div>
                  <div className="h-2 bg-gray-200 rounded mt-1">
                    <div
                      className="h-2 bg-[#F5A300] rounded"
                      style={{
                        width: progressBarWidth(
                          readiness.milestonesHit,
                          readiness.milestonesTotal,
                        ),
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SECTION 5 — DEADLINE + FEEDBACK CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* NEXT DEADLINE */}
          <div className="bg-white border border-gray-200 rounded-[16px] p-6 flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path
                  d="M7 2V5"
                  stroke="#D97706"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M17 2V5"
                  stroke="#D97706"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M3 9H21"
                  stroke="#D97706"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <rect
                  x="3"
                  y="5"
                  width="18"
                  height="16"
                  rx="2"
                  stroke="#D97706"
                  strokeWidth="2"
                />
              </svg>
            </div>
            <div>
              <p className="text-xs font-normal text-[#8B93A8]">
                NEXT DEADLINE
              </p>
              {/* ✅ Dynamic deadline date */}
              <p className="text-lg font-medium text-[#1A1F36] mt-1">
                {activeHackathon?.nextPhaseDate || "—"}
              </p>
              <p className="text-sm text-[#8B93A8] mt-1">
                {activeHackathon
                  ? `${activeHackathon.nextPhaseName} closes in ${activeHackathon.daysLeft} day${activeHackathon.daysLeft !== 1 ? "s" : ""}`
                  : "No active hackathon"}
              </p>
            </div>
          </div>

          {/* MENTOR FEEDBACK */}
          <div className="bg-white border border-gray-200 rounded-[16px] p-6 flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 4H20V16H6L4 18V4Z"
                  stroke="#7C3AED"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <p className="text-xs font-normal text-[#8B93A8]">
                MENTOR FEEDBACK
              </p>
              {/* ✅ Dynamic pending count */}
              <p className="text-lg font-medium text-[#1A1F36] mt-1">
                {feedbackInfo
                  ? `${feedbackInfo.pendingCount} Pending Review${feedbackInfo.pendingCount !== 1 ? "s" : ""}`
                  : "—"}
              </p>
              <p className="text-sm text-[#8B93A8] mt-1">
                {feedbackInfo?.latestReviewerName
                  ? `${feedbackInfo.latestReviewerName} reviewed your idea`
                  : feedbackInfo?.pendingCount
                    ? "Ideas awaiting mentor review"
                    : "No pending reviews"}
              </p>
            </div>
          </div>
        </div>

        {/* SECTION 6 — CHARTS */}
        <div className="flex flex-col lg:flex-row gap-2">

          {/* MONTHLY TRENDS */}
          <div className="bg-white border border-[#E4E8EE] rounded-[20px] shadow-[0_1px_3px_rgba(26,31,54,0.04)] p-8 w-full lg:w-[60%] h-[515px] flex flex-col">
            <p className="text-[10px] uppercase tracking-[1.6px] text-[#8B93A8]">Innovation Activity</p>
            <p className="text-[18px] text-[#1A1F36] mb-4">Monthly Trends</p>
            <div className="w-full flex-1 -ml-10">
              {/* ✅ Real data passed as prop - Legend now built into chart */}
              <WeeklyTrendsChart data={monthlyTrends} timeframe="monthly" />
            </div>
            {/* ✅ Styled legend matching By Status section */}
            {monthlyTrends.length > 0 && (
              <div className="mt-2 pt-3 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-600 mb-2">Activity Metrics:</p>
                <div className="space-y-1.5">
                  {[
                    { label: "Ideas", color: "#0058AB", value: monthlyTrends[monthlyTrends.length - 1]?.ideas || 0 },
                    { label: "Teams", color: "#00D5D0", value: monthlyTrends[monthlyTrends.length - 1]?.teams || 0 }
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-2 justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-[11px] text-[#4A5068]">{item.label}</span>
                      </div>
                      <span className="text-[11px] text-[#6E7891]">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* BY STATUS */}
          <div className="bg-white border rounded-[16px] p-6 lg:p-8 w-full lg:w-[40%] min-h-[320px]   flex flex-col">
            <p className="text-[10px] uppercase tracking-[1.6px] text-[#8B93A8]">
              Idea Breakdown
            </p>
            <p className="text-[18px] text-[#1A1F36] mb-4">By Status</p>
            <div className="w-full h-[350px] flex items-center justify-center">
              {/* ✅ Real data passed as prop - Legend now built into chart */}
              <StatusDonutChart data={ideaBreakdown} />
            </div>
            {ideaBreakdown.length === 0 && !loading && (
              <p className="text-xs text-gray-400 text-center">No ideas submitted yet</p>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
